import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type CookieConsent = 'accepted' | 'declined' | null;

interface CookiePreferences {
  preferences: boolean;  // Language, cookie consent choice
  analytics: boolean;    // Google Analytics
}

interface CookieConsentContextType {
  cookieConsent: CookieConsent;
  cookiePreferences: CookiePreferences;
  setCookieConsent: (consent: CookieConsent, preferences?: CookiePreferences) => void;
  hasConsented: boolean;
  showContent: boolean;
  showBanner: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [cookieConsent, setCookieConsentState] = useState<CookieConsent>(null);
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>({
    preferences: false,
    analytics: false,
  });
  const [showContent, setShowContent] = useState(true);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a cookie choice
    const savedConsent = localStorage.getItem('cookieConsent') as CookieConsent;
    const savedPreferences = localStorage.getItem('cookiePreferences');

    if (savedConsent) {
      // User has already made a choice - don't show banner
      setCookieConsentState(savedConsent);
      setShowBanner(false);
      setShowContent(true);

      if (savedPreferences) {
        try {
          const preferences = JSON.parse(savedPreferences);
          setCookiePreferences(preferences);
        } catch (e) {
          // Fallback to default preferences
          setCookiePreferences({ preferences: false, analytics: false });
        }
      }
    } else {
      // No choice made yet - show banner
      setShowBanner(true);
      setShowContent(true);
    }
  }, []);

  const setCookieConsent = (consent: CookieConsent, preferences?: CookiePreferences) => {
    setCookieConsentState(consent);

    if (consent === 'accepted') {
      const finalPreferences = preferences || { preferences: false, analytics: false };
      setCookiePreferences(finalPreferences);

      // Save the choice and preferences to localStorage
      localStorage.setItem('cookieConsent', consent);
      localStorage.setItem('cookiePreferences', JSON.stringify(finalPreferences));

      // Store cookie consent data for compliance
      localStorage.setItem('cookieConsentData', JSON.stringify({
        timestamp: new Date().toISOString(),
        consent: 'accepted',
        preferences: finalPreferences
      }));

      // Hide the banner
      setShowBanner(false);
      setShowContent(true);
    } else if (consent === 'declined') {
      // Clear all stored data when declined
      localStorage.removeItem('cookieConsent');
      localStorage.removeItem('cookiePreferences');
      localStorage.removeItem('language');
      localStorage.removeItem('cookieConsentData');

      // Reset preferences to default
      setCookiePreferences({ preferences: false, analytics: false });

      // Hide the banner
      setShowBanner(false);
      setShowContent(true);
    }
  };

  const hasConsented = cookieConsent === 'accepted';

  return (
    <CookieConsentContext.Provider value={{
      cookieConsent,
      cookiePreferences,
      setCookieConsent,
      hasConsented,
      showContent,
      showBanner,
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
